import React, { useState, useEffect } from 'react';
import api from '../../../components/api';
import './WriteSettingsPanel.css';
import '../../../styles/form.css';

const WriteSettingsPanel = ({ projectId, initialSettings, onSettingsUpdate, onClose }) => {
    const [language, setLanguage] = useState(initialSettings?.language || 'english');
    const [description, setDescription] = useState(initialSettings?.description || '');
    const [writingStyle, setWritingStyle] = useState(initialSettings?.writing_style || '');
    const [tone, setTone] = useState(initialSettings?.tone || 'Informative');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [nsfw, setNsfw] = useState(initialSettings?.nsfw || false);

    const languageOptions = [
        { name: 'afrikaans', flag: '🇿🇦' },
        { name: 'arabic', flag: '🇸🇦' },
        { name: 'bengali', flag: '🇧🇩' },
        { name: 'bulgarian', flag: '🇧🇬' },
        { name: 'chinese simplified', flag: '🇨🇳' },
        { name: 'chinese traditional', flag: '🇹🇼' },
        { name: 'croatian', flag: '🇭🇷' },
        { name: 'czech', flag: '🇨🇿' },
        { name: 'danish', flag: '🇩🇰' },
        { name: 'dutch', flag: '🇳🇱' },
        { name: 'english', flag: '🇬🇧' },
        { name: 'finnish', flag: '🇫🇮' },
        { name: 'french', flag: '🇫🇷' },
        { name: 'german', flag: '🇩🇪' },
        { name: 'greek', flag: '🇬🇷' },
        { name: 'hebrew', flag: '🇮🇱' },
        { name: 'hindi', flag: '🇮🇳' },
        { name: 'hungarian', flag: '🇭🇺' },
        { name: 'indonesian', flag: '🇮🇩' },
        { name: 'italian', flag: '🇮🇹' },
        { name: 'japanese', flag: '🇯🇵' },
        { name: 'korean', flag: '🇰🇷' },
        { name: 'malay', flag: '🇲🇾' },
        { name: 'marathi', flag: '🇮🇳' },
        { name: 'norwegian', flag: '🇳🇴' },
        { name: 'persian', flag: '🇮🇷' },
        { name: 'polish', flag: '🇵🇱' },
        { name: 'portuguese', flag: '🇵🇹' },
        { name: 'portuguese brazilian', flag: '🇧🇷' },
        { name: 'romanian', flag: '🇷🇴' },
        { name: 'russian', flag: '🇷🇺' },
        { name: 'serbian', flag: '🇷🇸' },
        { name: 'slovak', flag: '🇸🇰' },
        { name: 'slovenian', flag: '🇸🇮' },
        { name: 'spanish', flag: '🇪🇸' },
        { name: 'swahili', flag: '🇰🇪' },
        { name: 'swedish', flag: '🇸🇪' },
        { name: 'tagalog', flag: '🇵🇭' },
        { name: 'tamil', flag: '🇮🇳' },
        { name: 'telugu', flag: '🇮🇳' },
        { name: 'thai', flag: '🇹🇭' },
        { name: 'turkish', flag: '🇹🇷' },
        { name: 'ukrainian', flag: '🇺🇦' },
        { name: 'urdu', flag: '🇵🇰' },
        { name: 'vietnamese', flag: '🇻🇳' }
    ];

    const toneOptions = [
        'Informative', 'Academic', 'Casual', 'Creative', 'Professional',
        'Humorous', 'Formal', 'Technical', 'Persuasive', 'Poetic'
    ];

    // Mettre à jour les états locaux si les props changent
    useEffect(() => {
        if (initialSettings) {
            setLanguage(initialSettings.language || 'english');
            setDescription(initialSettings.description || '');
            setWritingStyle(initialSettings.writing_style || '');
            setTone(initialSettings.tone || 'Informative');
            setNsfw(!!initialSettings.nsfw);
        }
    }, [initialSettings]);

    // Sauvegarder les paramètres d'écriture
    const handleSaveSettings = async () => {
        try {
            setIsLoading(true);
            setError('');
            setSuccessMessage('');

            await api.post('/update_write_settings', {
                project_id: projectId,
                language,
                description,
                writing_style: writingStyle,
                tone,
                nsfw
            });

            const updatedSettings = {
                language,
                description,
                writing_style: writingStyle,
                tone,
                nsfw
            };

            // Mettre à jour l'état du parent
            if (onSettingsUpdate) {
                onSettingsUpdate(updatedSettings);
            }

            setSuccessMessage('Settings saved successfully');
            setTimeout(() => setSuccessMessage(''), 3000); // Masquer le message après 3 secondes
        } catch (err) {
            console.error('Error saving write settings:', err);
            setError(err.response?.data?.message || 'Error while saving settings');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="write-settings-panel">
            <div className="write-settings-header">
                <h2>Write Settings</h2>
                {onClose && (
                    <button className="panel-close-button" onClick={onClose} aria-label="Close panel">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                )}
            </div>

            {error && <div className="write-settings-error">{error}</div>}
            {successMessage && <div className="write-settings-success">{successMessage}</div>}

            <div className="write-settings-form">
                <div className="form-group">
                    <div className="nsfw-field">
                    <input
                            id="nsfw"
                            className="nsfw-checkbox"
                            type="checkbox"
                            checked={nsfw}
                            onChange={(e) => setNsfw(e.target.checked)}
                            disabled={isLoading}
                            aria-label="NSFW"
                        />
                        <label htmlFor="nsfw" className="nsfw-label">NSFW</label>
                        
                    </div>
                    <div className="info-text">
                        <span className="material-symbols-outlined info-icon">info</span>
                        <span>Generate and rewrite content with NSFW models.</span>
                    </div>
                </div>
                <div className="form-group">
                    <label htmlFor="language">Language</label>
                    <select
                        id="language"
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        disabled={isLoading}
                        className="form-control"
                    >
                        {languageOptions.map(lang => (
                            <option key={lang.name} value={lang.name}>{lang.name.charAt(0).toUpperCase() + lang.name.slice(1)} {lang.flag}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label htmlFor="tone">Tone</label>
                    <select
                        id="tone"
                        value={tone}
                        onChange={(e) => setTone(e.target.value)}
                        disabled={isLoading}
                        className="form-control"
                    >
                        {toneOptions.map(t => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label htmlFor="description">Book Description</label>
                    <div className="info-text">
                        <span className="material-symbols-outlined info-icon">info</span>
                        <span>The main topic of your book. This description will be used to generate content and outline.</span>
                    </div>
                    <textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        disabled={isLoading}
                        className="form-control"
                        placeholder="Describe your project..."
                        rows={4}
                    ></textarea>
                </div>

                <div className="form-group">
                    <label htmlFor="writingStyle">Writing Style Example</label>
                    <div className="info-text">
                        <span className="material-symbols-outlined info-icon">info</span>
                        <span>Provide a sample of the writing style you prefer. The AI will follow this style when generating or rewriting text.</span>
                    </div>
                    <textarea
                        id="writingStyle"
                        value={writingStyle}
                        onChange={(e) => setWritingStyle(e.target.value)}
                        disabled={isLoading}
                        className="form-control"
                        placeholder="Give an example of the writing style you want to follow..."
                        rows={6}
                    ></textarea>
                </div>

                <div className="form-actions">
                    <button 
                        onClick={handleSaveSettings}
                        className="button-plain"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WriteSettingsPanel; 